import { CanvasElement, GlobalSettings } from '@/lib/engineTypes';
import { buildVFXLayers, VFXLayer } from '@/lib/vfxLayerEngine';

export type ValidationResult = {
  status: 'valid' | 'warning' | 'conflict';
  conflicts: string[];
  warnings: string[];
};

// Configuration object expected by the validator. We keep it loose to allow
// the caller to pass whatever context is convenient (elements/global settings/
// style, etc.). Only the properties used by the rules are accessed.
interface ValidatorConfig {
  elements?: CanvasElement[];
  globalSettings?: GlobalSettings;
  style?: string;
}

export function validatePrompt(config: any): ValidationResult {
  const { elements = [], globalSettings = {} as GlobalSettings, style = '' } = config as ValidatorConfig;
  const conflicts: string[] = [];
  const warnings: string[] = [];

  // rule: 24mm lens + extreme close-up
  const cam = (elements as CanvasElement[]).find(e => e.type === 'camera');
  if (cam) {
    const lens = cam.properties.lens;
    const shot = cam.properties.shotType;
    if (lens === '24mm' && shot === 'extreme_close_up') {
      conflicts.push('24mm lens cannot be used for extreme close-up framing');
    }
  }

  // rule: symmetry + ruleOfThirds both true
  if (globalSettings.symmetryMode && globalSettings.ruleOfThirds) {
    conflicts.push('Symmetrical composition cannot be combined with rule of thirds');
  }

  // rule: heavy fog + microDetail enabled
  // we interpret heavy fog as fogHaze > 60
  if (globalSettings.fogHaze > 60 && globalSettings.microDetail > 70) {
    conflicts.push('Heavy fog reduces micro-detail visibility');
  }

  // rule: DSLR + Unreal engine mixed
  const isUnreal = ['unreal_engine', 'aaa_game'].includes(globalSettings.renderStyle);
  if (isUnreal && cam && cam.properties.cameraType === 'dslr') {
    conflicts.push('DSLR capture cannot be mixed with Unreal Engine rendering style');
  }

  // rule: high-key lighting + noir lighting
  const hasHighKey = (elements as CanvasElement[]).some(e =>
    e.type === 'light' &&
    (e.properties.lightPreset === 'high_key' || e.properties.lightStyle === 'high_key')
  );
  const hasNoir = style.includes('noir') || String(globalSettings.renderStyle).includes('noir');
  if (hasHighKey && hasNoir) {
    conflicts.push('High-key lighting conflicts with noir lighting mood');
  }

  // rule: more than 6 VFX effects (warning)
  try {
    const layers = buildVFXLayers(globalSettings as GlobalSettings);
    const totalEffects = layers.reduce((sum, l) => sum + l.effects.length, 0);
    if (totalEffects > 6) {
      warnings.push('More than six heavy VFX effects active; consider reducing to maintain clarity');
    }
  } catch {
    // if buildVFXLayers fails for any reason just ignore this rule
  }

  let status: ValidationResult['status'] = 'valid';
  if (conflicts.length > 0) status = 'conflict';
  else if (warnings.length > 0) status = 'warning';

  return { status, conflicts, warnings };
}
